import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { ArrowUpIcon, Check, FileIcon, PlusIcon, SquareIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { buildReferencedFileName } from "~/ai-chat/model/chat-message";
import { isReadableFileType } from "~/ai-chat/model/readable-file-types";
import { ModelPicker } from "~/ai-chat/ui/model-picker";
import { useUploadResource } from "~/resource/data-source/use-upload-resource";
import { routes } from "~/routes";
import { AddFilesModal } from "~/upload/feature/add-files-modal";
import UploadSpecModal from "~/upload/ui/upload-spec-modal";
import { useFileUpload } from "~/upload/use-file-upload";
import { cn } from "~/utils/cn";

// Hard client-side cap on a single message. Generous enough for pasted context,
// low enough to keep an accidental giant paste from ballooning the request.
const MAX_MESSAGE_CHARS = 8000;
// Start showing the counter this many characters before the cap.
const COUNTER_THRESHOLD = 500;

interface AiChatComposerProps {
  onSend: (content: string) => void;
  onStop: () => void;
  isPending: boolean;
}

export function AiChatComposer({ onSend, onStop, isPending }: AiChatComposerProps) {
  const [draft, setDraft] = useState("");
  const {
    referencedFiles,
    addReferencedFiles,
    removeReferencedFile,
    clearReferencedFiles,
    restoredDraft,
    setRestoredDraft,
    selectedModelId,
    setSelectedModelId,
  } = useAiChat();

  // True only while the box holds an untouched restored draft (see below).
  const holdsRestoredDraft = useRef(false);

  // A failed send hands the typed message back (see useChatSend). Only fill an
  // empty composer — never overwrite something the user has typed since.
  useEffect(() => {
    if (restoredDraft === null) return;
    setDraft((current) => {
      if (current.length > 0) return current;
      holdsRestoredDraft.current = true;
      return restoredDraft;
    });
    setRestoredDraft(null);
  }, [restoredDraft, setRestoredDraft]);

  // A Retry re-send bypasses the composer, so a lingering restored draft would
  // set up an accidental double-send. Clear it when the turn starts — but only
  // the restored draft itself, never text the user has typed since.
  useEffect(() => {
    if (isPending && holdsRestoredDraft.current) {
      setDraft("");
      holdsRestoredDraft.current = false;
    }
  }, [isPending]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addFilesModalOpen, setAddFilesModalOpen] = useState(false);
  const textareaId = useId();

  // Upload confirmation lives inside the chat panel (not a global toast) so it appears in the same
  // window as the chat — including the popped-out floating window, which a root-level sonner toast
  // can't reach — and clears itself, since the file chips below are the lasting confirmation.
  const [addedNotice, setAddedNotice] = useState<string | null>(null);
  const addedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
    },
    [],
  );
  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !isPending;
  // Attachments whose contents the agent can't read yet — warn, never block.
  const unreadableFiles = referencedFiles.filter((file) => !isReadableFileType(file.name));

  const { uploadFiles } = useUploadResource({
    onComplete: ({ files }) => {
      const uploaded = files.flatMap((file) =>
        file.state === "success" && file.resourceId
          ? [
              {
                resourceId: file.resourceId,
                name: buildReferencedFileName(file.displayName, file.extension),
                fileRevisionId: file.fileRevisionId,
                origin: "attachment" as const,
              },
            ]
          : [],
      );
      if (uploaded.length > 0) {
        addReferencedFiles(uploaded);
        setAddedNotice(
          uploaded.length === 1
            ? `Added ${uploaded[0].name} to your library`
            : `Added ${uploaded.length} resources to your library`,
        );
        if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
        addedTimerRef.current = setTimeout(() => setAddedNotice(null), 3500);
      }
    },
  });
  const { isModalOpen, filesToUpload, handleCloseModal, handleUploadButtonClick, handleFileInputChange } =
    useFileUpload();

  const submit = () => {
    if (!canSend) return;
    onSend(trimmed);
    setDraft("");
    holdsRestoredDraft.current = false;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="border-border shrink-0 border-t-[0.5px]">
      {referencedFiles.length > 0 ? (
        <div className="px-3 pt-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span
              className={cn(
                "flex items-center gap-1 text-[11px] font-medium transition-colors",
                addedNotice ? "text-primary" : "text-muted-foreground",
              )}
            >
              <Check className="size-3 shrink-0" />
              {addedNotice ?? "In your library"}
            </span>
            <Button
              type="button"
              variant="link"
              size="2xs"
              onClick={clearReferencedFiles}
              className="text-muted-foreground h-auto px-0 hover:no-underline"
            >
              Clear all
            </Button>
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {referencedFiles.map((file) => (
              <li
                key={file.resourceId}
                className="bg-accent flex max-w-full items-center gap-1.5 rounded-md py-1 pr-1 pl-2 text-xs"
              >
                <FileIcon className="text-muted-foreground size-3.5 shrink-0" />
                <Link
                  to={`${routes.Resources}/${file.resourceId}`}
                  className="truncate underline-offset-2 hover:underline"
                  title={`Open ${file.name} in your library`}
                >
                  {file.name}
                </Link>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-2xs"
                      onClick={() => removeReferencedFile(file.resourceId)}
                      aria-label={`Remove ${file.name} from this chat`}
                      className="shrink-0"
                    >
                      <XIcon className="size-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Remove from this chat — the resource stays in your library</TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ul>
          {unreadableFiles.length > 0 ? (
            <p className="text-muted-foreground mt-1.5 flex items-start gap-1 text-[11px] leading-snug">
              <TriangleAlertIcon className="mt-px size-3 shrink-0" />
              <span>
                Istari AI can reference {unreadableFiles.length === 1 ? "this file" : "these files"} but can&apos;t read{" "}
                {unreadableFiles.length === 1 ? "its" : "their"} contents yet:{" "}
                <span className="font-medium">{unreadableFiles.map((file) => file.name).join(", ")}</span>
              </span>
            </p>
          ) : null}
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="px-3 py-2.5">
        <label htmlFor={textareaId} className="sr-only">
          Message Istari AI
        </label>
        <div
          className={cn(
            "border-input bg-background flex flex-col rounded-lg border shadow-xs transition-colors",
            "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
          )}
        >
          <textarea
            id={textareaId}
            value={draft}
            onChange={(event) => {
              holdsRestoredDraft.current = false;
              setDraft(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Write a message"
            rows={1}
            maxLength={MAX_MESSAGE_CHARS}
            disabled={isPending}
            className={cn(
              "placeholder:text-muted-foreground field-sizing-content resize-none bg-transparent",
              "max-h-32 px-3 pt-2.5 pb-1 text-sm leading-snug outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          />
          <div className="flex items-center justify-between gap-2 px-2.5 pt-1 pb-2">
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setAddFilesModalOpen(true)}
                    disabled={isPending}
                    aria-label="Upload files"
                    className="text-muted-foreground size-6"
                  >
                    <PlusIcon className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Upload files</TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              <ModelPicker selectedModelId={selectedModelId} onSelect={setSelectedModelId} disabled={isPending} />
              {isPending ? (
                <Button
                  type="button"
                  size="icon-sm"
                  onClick={onStop}
                  aria-label="Stop generating"
                  className="size-8 shrink-0 rounded-full"
                >
                  <SquareIcon className="size-3.5 fill-current" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="icon-sm"
                  disabled={!canSend}
                  aria-label="Send message"
                  className="size-8 shrink-0 rounded-full"
                >
                  <ArrowUpIcon className="size-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
        {draft.length > MAX_MESSAGE_CHARS - COUNTER_THRESHOLD ? (
          <div className="text-muted-foreground pt-1.5 text-right text-[10px] tabular-nums">
            {draft.length.toLocaleString()} / {MAX_MESSAGE_CHARS.toLocaleString()}
          </div>
        ) : null}
        <p className="text-muted-foreground pt-2 text-center text-[10px] leading-4">
          These models can make mistakes. Please double-check cited sources.
        </p>
      </form>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        aria-hidden="true"
      />
      <AddFilesModal
        open={addFilesModalOpen}
        onOpenChange={setAddFilesModalOpen}
        onUploadClick={() => handleUploadButtonClick(fileInputRef)}
        onConnectUpload={uploadFiles}
      />
      {isModalOpen && filesToUpload ? (
        <UploadSpecModal
          files={filesToUpload}
          open={isModalOpen}
          setModalOpen={handleCloseModal}
          onFilesUploaded={uploadFiles}
        />
      ) : null}
    </div>
  );
}
